import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OrgPluginsAPI } from '@makyoapp/makyo-sdk'
import { PluginAdapter } from '@makyoapp/makyo-common'
import { ModalGrid, PluginCard } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'

import PluginRequirements from './Requirements/Requirements'

interface PluginDetailModalProps {
  /**
   * the current plugin data
   */
  plugin: PluginAdapter
  /**
   * saved backend values for this credential
   */
  savedBackendValues?: Record<string, any>
  show: boolean
  onClose: () => void
}

/**
 * PluginDetailModal is a component that displays detailed information about a specific plugin in a modal.
 *
 * @param {PluginDetailModalProps} props - The properties that define the plugin details.
 * @param {Plugin} props.plugin - The plugin object containing the details to be displayed.
 *
 * @example
 * <PluginDetailModal plugin={plugin} />
 *
 * @returns {JSX.Element} A modal containing detailed information about a plugin.
 */
export default function PluginDetailModal({ plugin, savedBackendValues = {}, show, onClose }: PluginDetailModalProps) {
  const { t } = useTranslation()
  const { currentOrg } = useCurrentOrgContext()

  // const tabKeys: PluginAdapterKey[] = ['details', 'instructions', 'screenshots', 'reviews']
  const [saving, setSaving] = useState<boolean>(false)
  const requirementsRef = useRef<{
    backend: Record<string, string>
    frontend: Record<string, string>
  }>({ backend: {}, frontend: {} })

  // ─── Functions ───────────────────────────────────────────────────────
  const handleRequirementsChange = (backendForm: Record<string, string>, frontendForm: Record<string, any>) => {
    requirementsRef.current = { backend: backendForm, frontend: frontendForm }
  }

  const handleSave = () => {
    if (!currentOrg) {
      return
    }

    setSaving(true)
    // console.log('Saving plugin details', requirementsRef.current)

    // const auth: IOAuth2PluginData = {}
    //   access_token: '123',
    //   refresh_token: '456',
    //   expires_in: 3600,
    // }

    OrgPluginsAPI.ReqAdd(currentOrg.id, {
      _Label: plugin.name,
      _PluginID: plugin.id,
      _Backend: requirementsRef.current.backend,
      _Frontend: requirementsRef.current.frontend,
    })
      .then(() => {
        toasted(t('Plugin details successfully saved'))
      })
      .catch((e) => {
        console.error(e)
        toasted(t('Error saving plugin details'), { type: 'error' })
      })
      .finally(() => {
        setSaving(false)
      })
  }

  // const shouldShowSaveButton = plugin.auth?.type !== 'oauth2'

  return (
    <ModalGrid
      show={show}
      onCancel={onClose}
      id={`plugin-detail-modal`}
      title={plugin.name}
      onConfirm={handleSave}
      onHide={onClose}
      isLoading={saving}
      // @ts-ignore
      noPadding
    >
      <div className='min-h-[400px] overflow-y-auto'>
        {/* Display plugin card with details */}
        <PluginCard
          className='p-8 bg-zinc-100 dark:bg-slate-900 border-b border-zinc-200 dark:border-slate-600'
          isBoxed={false}
          name={plugin.name}
          version={''}
          description={plugin.description}
          icon={<img alt={plugin.name} src={plugin.icon} width={48} height={48} />}
        />

        {/* Display plugin requirements */}
        <div className='p-8'>
          <PluginRequirements
            backendRequirements={savedBackendValues}
            backend={plugin.backend}
            frontend={plugin.frontend}
            onChange={handleRequirementsChange}
          />
        </div>
      </div>

      {/* <Modal.Footer className='justify-end'>
        {plugin.isInstalled ? (
          <Button disabled={saving} onClick={handleSave}>
            {saving ? <span className='loading spin' /> : t('Save')}
          </Button>
        ) : plugin.price === 'free' ? ( // Check if the plugin is free
          <Button>{t('install')}</Button>
        ) : (
          <div className='flex-c'>
            <SelectDropdown
              value={payScheme}
              outlined={true}
              onChange={(o, e) => {
                e.stopPropagation()
                setPayScheme(o as 'monthly' | 'once') // Update payment scheme on change
              }}
              renderOption={(e) => {
                // Render the price in the dropdown options
                if (e === 'monthly') {
                  return (
                    <span>
                      <strong>{priceFmtNoDecimal.format((plugin.price as PluginPricingSchemeAdapter).monthly)}</strong>{' '}
                      /{t('perMonth')}
                    </span>
                  )
                } else {
                  return (
                    <span>
                      <strong>{priceFmtNoDecimal.format((plugin.price as PluginPricingSchemeAdapter).once)}</strong>{' '}
                      once
                    </span>
                  )
                }
              }}
              options={['monthly', 'once']} // Payment options
            />
            <Button variant={ButtonVariant.Normal}>
              <span className='txt'>{payScheme === 'monthly' ? t('Subscribe & Install') : t('Pay & Install')}</span>
            </Button>
          </div>
        )}
      </Modal.Footer> */}
    </ModalGrid>
  )
}
